import * as moment from 'moment';
import * as React from 'react';
import { computed } from 'mobx';
import { EnumStore } from '../../stores';
import { inject, observer } from 'mobx-react';
import { LoadingIndicator, LoginRestriction, PonderaModal } from '..';
import { NotificationService } from '../../services';
import { ServiceStore, OrganizationStore, FilterStore, UserStore } from '../../stores';
import { SideNavMenu } from '../navigation';
import { StorageKeys } from '../../stores/Constants';

interface AuthenticatedLayoutState {
    notificationService?: NotificationService;
    showBannerModal: boolean;
}

interface AuthenticatedLayoutProps {
    ServiceStore?: ServiceStore;
    OrganizationStore?: OrganizationStore;
    FilterStore?: FilterStore;
    UserStore?: UserStore;
    EnumStore?: EnumStore;
}

const ScrollToTop = () => {
    window.scrollTo(0, 0);
    return null;
};

@inject('ServiceStore', 'OrganizationStore', 'UserStore', 'EnumStore', 'FilterStore') @observer
export class AuthenticatedLayout extends React.Component<AuthenticatedLayoutProps, AuthenticatedLayoutState> {
    private sideMenuOpen: boolean = true;
    private preload: boolean = true;

    @computed get storesLoaded() {
        return this.props.UserStore!.userLoaded
            && this.props.EnumStore!.enumsLoaded;
    }

    @computed get errorLoadingStores() {
        return this.props.UserStore!.errorLoadingUser
            || this.props.EnumStore!.errorLoadingEnums;
    }

    @computed get userIsDeactivated() {
        const { isDeactivated, isDeleted } = this.props.UserStore!.userInfo;
        return isDeactivated || isDeleted;
    }

    @computed get organizationIsDeactivated() {
        const { isDeactivated, isDeleted } = this.props.UserStore!.userInfo.organization;
        return isDeactivated || isDeleted;
    }

    @computed get organizationIsExpired() {
        const { expirationDate } = this.props.UserStore!.userInfo.organization;
        return moment().isAfter(moment(expirationDate));
    }

    @computed get userIsBeingImpersonated() {
        return this.props.UserStore!.userInfo.isBeingImpersonated;
    }

    @computed get noSurveys() {
        return this.props.FilterStore!.noSurveys;
    }

    constructor(props: AuthenticatedLayoutProps) {
        super(props);

        const showBannerModal = localStorage.getItem(StorageKeys.MaintenanceModalDismissed) !== 'true';

        this.state = { 
            notificationService: this.props.ServiceStore!.notificationService,
            showBannerModal: showBannerModal
        };

        this.sideMenuOpen = window.innerWidth > 768;
        if (this.sideMenuOpen) {
            document.body.classList.add('side-menu-open');
        } else {
            document.body.classList.remove('side-menu-open');
        }

        this.props.UserStore!.loadUser().then(() => {
            this.props.FilterStore!.SetUserSurveyReportType(this.props.UserStore!.userInfo.userReportTypes);
        });
        this.props.OrganizationStore!.loadOrganizations();
        this.props.EnumStore!.loadEnums();
    }

    componentDidMount() {
        window.addEventListener('side-menu-toggle', this.sideMenuToggle.bind(this), false);
        this.preload = false;
    }

    componentWillUnmount() {
        window.removeEventListener('side-menu-toggle', this.sideMenuToggle.bind(this), false);
    }

    private sideMenuToggle() {
        this.sideMenuOpen = !this.sideMenuOpen;
        if (this.sideMenuOpen) {
            document.body.classList.add('side-menu-open');
        } else {
            document.body.classList.remove('side-menu-open');
        }
        this.forceUpdate();
    }

    private hideBannerModal = () => {
        localStorage.setItem(StorageKeys.MaintenanceModalDismissed, 'true');
        this.setState({ showBannerModal: false });
    }

    public render() {
        if (this.errorLoadingStores) {
            const props = { header: `Initialization Error`, text: `There was an error initializing data for Pondera Virtual Advisor. Please try reloading the page.` };
            return <LoginRestriction  {...props} />;
        }

        if (!this.storesLoaded) {
            return (
            <div>
                {this.props.FilterStore && <div id={`noSurveys-${this.props.FilterStore.noSurveys}`} />}
                <LoadingIndicator />
            </div>);
        }

        if (this.organizationIsExpired && !this.userIsBeingImpersonated) {
            const props = { header: `Organization Expired`, text: `Your organization's access to the Pondera Virtual Advisor has expired. Please contact support for assistance.` };
            return <LoginRestriction  {...props} />;
        }

        if (this.userIsDeactivated && !this.userIsBeingImpersonated) {
            const props = { header: `User Deactivated`, text: `Your access to the Pondera Virtual Advisor has been deactivated. Please contact your organization's administrator for assistance.` };
            return <LoginRestriction  {...props} />;
        }

        if (this.organizationIsDeactivated && !this.userIsBeingImpersonated) {
            const props = { header: `Organization Deactivated`, text: `Your organization's access to the Pondera Virtual Advisor has been deactivated. Please contact support for assistance.` };
            return <LoginRestriction  {...props} />;
        }

        const { children } = this.props;

        var containerClass = 'react-app-container';
        if (this.preload) {
            containerClass += ' preload';
        }
        if (!this.sideMenuOpen) {
            containerClass += ' full';
        }

        const childrenWithProps = React.Children.map(children, (child: any) => {
            const routeChildren = React.Children.map(child.props.children, (routeChild) => {
                return React.cloneElement(routeChild as React.ReactElement<any>, { notificationService: this.state.notificationService });
            });

            return React.cloneElement(child as React.ReactElement<any>, { children: routeChildren });
        });

        return (
            <div className={containerClass}>
                <ScrollToTop />
                {this.state.notificationService && <SideNavMenu open={this.sideMenuOpen} notificationService={this.state.notificationService} />}
                <div className={`site-content ${this.sideMenuOpen ? 'open' : ''}`}>
                    <div className="site-content-inner">
                        {this.noSurveys && <div>
                            <br />
                            <h1>No Surveys have been assigned to this organization.</h1></div>}
                        {!this.noSurveys && this.props.FilterStore!.surveyVersion === undefined && <LoadingIndicator />}
                        {this.props.FilterStore!.surveyVersion !== undefined && childrenWithProps}
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/layout/AuthenticatedLayout.tsx